import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Drawer,
  TextField,
  Snackbar,
  Alert,
  Chip,
  useTheme,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Avatar,
  ListItemAvatar,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import DatePicker from '../../shared/components/forms/HotelDatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import MeetingRoomRoundedIcon from '@mui/icons-material/MeetingRoomRounded';
import HotelRoundedIcon from '@mui/icons-material/HotelRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import TimelapseRoundedIcon from '@mui/icons-material/TimelapseRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import moment from 'moment';
import {
  getRoomCleanings,
  createRoomCleaning,
  updateRoomCleaning,
  deleteRoomCleaning,
  getHousekeepingSettings,
  getRoomCleaning,
} from '../../services/api';
import { getConfigurationRooms, getHousekeepingStaff } from '../../services/api';
import HousekeepingCleaningChecklist, {
  alignCheckedIdsToItems,
  areRequiredChecklistItemsChecked,
  checklistStateFromCleaning,
  cleaningStatusFromChecklist,
  normalizeChecklistItems,
  pickRichestOpenChecklistState,
  shouldShowCleaningChecklist,
} from './HousekeepingCleaningChecklist';
import {
  getHousekeepingDrawerFooterSx,
  getHousekeepingDrawerHeaderSx,
  getHousekeepingDrawerPaperSx,
  getHousekeepingListPaperSx,
  getHousekeepingThemeColors,
} from '../../utils/housekeepingMobileLayout';
import {
  createDrawerOverlayProps,
  getDrawerRootSx,
} from '../../utils/drawerOverlayProps';
import { detectThemeSync } from '../../utils/themeUtils';
import {
  getSoftFieldSx,
  SectionLabel,
  SelectableChip,
  getDrawerPrimaryButtonSx,
  getDrawerCancelButtonSx,
} from './housekeepingFormKit';

const THEME_COLOR = '#4ce1b6';

const CLEANING_TYPE_CHOICES = [
  {
    value: 'STAYOVER',
    label: 'Stay Over',
    color: '#42a5f5',
    icon: <HotelRoundedIcon />,
  },
  {
    value: 'CHECKOUT',
    label: 'Check Out',
    color: '#ff7043',
    icon: <LogoutRoundedIcon />,
  },
  {
    value: 'DEEP',
    label: 'Deep Clean',
    color: '#ab47bc',
    icon: <AutoAwesomeRoundedIcon />,
  },
];

function CleaningTypeChip({ type, isDark = false }) {
  const meta = CLEANING_TYPE_CHOICES.find((choice) => choice.value === type) || {
    label: type || 'Cleaning',
    color: '#78909c',
    icon: <CleaningServicesIcon />,
  };
  return (
    <Chip
      size="small"
      icon={React.cloneElement(meta.icon, { sx: { fontSize: 15 } })}
      label={meta.label}
      sx={{
        height: 26,
        maxWidth: '100%',
        fontWeight: 800,
        fontSize: '0.72rem',
        letterSpacing: '0.02em',
        bgcolor: alpha(meta.color, isDark ? 0.2 : 0.12),
        color: isDark ? alpha(meta.color, 0.95) : meta.color,
        border: `1px solid ${alpha(meta.color, isDark ? 0.5 : 0.38)}`,
        boxShadow: isDark ? 'none' : `0 1px 0 ${alpha(meta.color, 0.12)}`,
        '& .MuiChip-icon': {
          color: meta.color,
          ml: 0.7,
        },
        '& .MuiChip-label': {
          px: 0.85,
        },
      }}
    />
  );
}

const STATUS_CHOICES = [
  {
    value: 'PENDING',
    label: 'Pending',
    color: '#ffa726',
    icon: <ScheduleRoundedIcon />,
  },
  {
    value: 'IN_PROGRESS',
    label: 'In Progress',
    color: '#29b6f6',
    icon: <TimelapseRoundedIcon />,
  },
  {
    value: 'COMPLETED',
    label: 'Completed',
    color: '#66bb6a',
    icon: <CheckCircleRoundedIcon />,
  },
];

const RoomCleaningSection = ({ onRefreshRoomStatus }) => {
  const theme = useTheme();
  const isDark = detectThemeSync() === 'dark';
  const themeColors = getHousekeepingThemeColors(isDark);
  const drawerOverlays = useMemo(
    () => createDrawerOverlayProps(isDark, isDark ? themeColors.surface : '#ffffff'),
    [isDark, themeColors.surface]
  );
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [cleaningList, setCleaningList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [drawerMode, setDrawerMode] = useState('add'); // add | edit | view
  const [editingCleaning, setEditingCleaning] = useState(null);
  const isViewMode = drawerMode === 'view';
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cleaningToDelete, setCleaningToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState(''); // Debounced value sent to API
  const [totalCount, setTotalCount] = useState(0);
  const [rooms, setRooms] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    room: '',
    staff: '',
    cleaning_date: moment(),
    start_time: null, // moment or null for TimePicker
    end_time: null,
    cleaning_type: 'STAYOVER',
    status: 'PENDING',
    remarks: '',
  });
  const [errors, setErrors] = useState({});
  const [checklistEnabled, setChecklistEnabled] = useState(false);
  const [checklistItems, setChecklistItems] = useState([]);
  const [savedChecklistItems, setSavedChecklistItems] = useState([]);
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const hasCapturedChecklist = savedChecklistItems.length > 0;
  const visibleChecklistItems = hasCapturedChecklist ? savedChecklistItems : checklistItems;
  const showCleaningChecklist = visibleChecklistItems.length > 0 && shouldShowCleaningChecklist({
    checklistEnabled,
    isViewMode,
    capturedItems: savedChecklistItems,
  });

  const refreshChecklistSettings = useCallback(() => {
    getHousekeepingSettings()
      .then((settings) => {
        const items = normalizeChecklistItems(settings?.cleaning_checklist, {
          defaultMandatory: Boolean(settings?.checklist_mandatory),
        });
        setChecklistEnabled(Boolean(settings?.checklist_enabled) && items.length > 0);
        setChecklistItems(items);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchCleanings();
    fetchRooms();
    fetchStaff();
    refreshChecklistSettings();
  }, []);

  const fetchRooms = async () => {
    try {
      const roomsData = await getConfigurationRooms();
      const allRooms = [];
      roomsData.forEach(floor => {
        const floorDisplay = floor.floor_display || floor.floor_name || `Floor ${floor.floor_number ?? ''}`.trim() || '—';
        if (floor.rooms && floor.rooms.length > 0) {
          floor.rooms.forEach(room => {
            allRooms.push({
              id: room.id,
              room_number: room.room_number,
              room_type_name: room.room_type_name,
              floor_display: floorDisplay,
              clean_status: (room.clean_status || 'clean').toLowerCase(),
              image_url: room.room_type_image_url || room.image_url || room.image || null,
              name: `${room.room_number} (${room.room_type_name}) - ${floorDisplay}`,
            });
          });
        }
      });
      setRooms(allRooms);
    } catch (error) {}
  };

  const fetchStaff = async () => {
    try {
      const response = await getHousekeepingStaff({ page_size: 1000 });
      setStaffList(response.results || []);
    } catch (error) {}
  };

  const fetchCleanings = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        page_size: rowsPerPage,
      };
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      const response = await getRoomCleanings(params);
      setCleaningList(response.results || []);
      setTotalCount(response.count ?? 0);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.userMessage || 'Failed to load room cleanings',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery]);

  // Debounce search: update searchQuery after delay and reset to first page
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchCleanings();
  }, [fetchCleanings]);

  const parseTimeToMoment = (t) => {
    if (!t) return null;
    if (moment.isMoment(t)) return t.isValid() ? t : null;
    const m = moment(t, ['HH:mm:ss', 'HH:mm', 'h:mm A', 'hh:mm A'], true);
    return m.isValid() ? m : null;
  };

  const applyCleaningToForm = (cleaning) => {
    const startM = parseTimeToMoment(cleaning.start_time);
    const endM = parseTimeToMoment(cleaning.end_time);
    const roomId = typeof cleaning.room === 'object' && cleaning.room != null
      ? cleaning.room.id
      : (cleaning.room_id ?? cleaning.room ?? '');
    const staffId = typeof cleaning.staff === 'object' && cleaning.staff != null
      ? cleaning.staff.id
      : (cleaning.staff_id ?? cleaning.staff ?? '');
    const roomValue = roomId === '' || roomId == null ? '' : (Number(roomId) || roomId);
    const staffValue = staffId === '' || staffId == null ? '' : (Number(staffId) || staffId);
    const saved = checklistStateFromCleaning(cleaning);
    const visibleItems = saved.items.length ? saved.items : checklistItems;
    setEditingCleaning(cleaning);
    setFormData({
      room: roomValue,
      staff: staffValue,
      cleaning_date: cleaning.cleaning_date ? moment(cleaning.cleaning_date) : moment(),
      start_time: startM,
      end_time: endM,
      cleaning_type: cleaning.cleaning_type || 'STAYOVER',
      status: cleaning.status || 'PENDING',
      remarks: cleaning.remarks || '',
    });
    setSavedChecklistItems(saved.items);
    setCheckedIds(alignCheckedIdsToItems(visibleItems, saved.checkedIds, saved.items));
  };

  const handleOpenDrawer = (cleaning = null, mode = null) => {
    const nextMode = mode || (cleaning ? 'edit' : 'add');
    setDrawerMode(nextMode);
    setErrors({});
    refreshChecklistSettings();
    if (cleaning) {
      applyCleaningToForm(cleaning);
      setOpenDrawer(true);
      if (cleaning.id) {
        getRoomCleaning(cleaning.id)
          .then(async (fresh) => {
            let next = fresh;
            if (checklistEnabled && checklistStateFromCleaning(fresh).checkedIds.size === 0) {
              const roomId = typeof fresh.room === 'object' && fresh.room != null
                ? fresh.room.id
                : (fresh.room_id ?? fresh.room);
              if (roomId && fresh.cleaning_date) {
                const related = await getRoomCleanings({
                  room_id: roomId,
                  cleaning_date: fresh.cleaning_date,
                  page_size: 50,
                }).catch(() => ({ results: [] }));
                const relatedRows = Array.isArray(related) ? related : (related?.results || []);
                const sibling = pickRichestOpenChecklistState(relatedRows, fresh.id);
                if (sibling.checkedIds.size) {
                  next = {
                    ...fresh,
                    checklist_item_ids: Array.from(sibling.checkedIds),
                    checklist_snapshot: sibling.items.length
                      ? sibling.items.map((item) => ({
                        ...item,
                        checked: sibling.checkedIds.has(item.id),
                      }))
                      : fresh.checklist_snapshot,
                  };
                }
              }
            }
            applyCleaningToForm(next);
          })
          .catch(() => {});
      }
      return;
    }
    setEditingCleaning(null);
    setSavedChecklistItems([]);
    setFormData({
      room: '',
      staff: '',
      cleaning_date: moment(),
      start_time: null,
      end_time: null,
      cleaning_type: 'STAYOVER',
      status: 'PENDING',
      remarks: '',
    });
    setCheckedIds(new Set());
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setDrawerMode('add');
    setEditingCleaning(null);
    setFormData({
      room: '',
      staff: '',
      cleaning_date: moment(),
      start_time: null,
      end_time: null,
      cleaning_type: 'STAYOVER',
      status: 'PENDING',
      remarks: '',
    });
    setSavedChecklistItems([]);
    setCheckedIds(new Set());
    setErrors({});
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'start_time' && value && moment.isMoment(value)) {
        next.end_time = value.clone().add(20, 'minutes');
        setErrors((e) => ({ ...e, end_time: null }));
      }
      return next;
    });
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.room) {
      newErrors.room = 'Room is required';
    }
    if (formData.staff === '' || formData.staff == null) {
      newErrors.staff = 'Staff is required';
    }
    if (!formData.cleaning_date) {
      newErrors.cleaning_date = 'Cleaning date is required';
    }
    if (!formData.cleaning_type) {
      newErrors.cleaning_type = 'Cleaning type is required';
    }
    const startM =
      formData.start_time && moment.isMoment(formData.start_time) && formData.start_time.isValid()
        ? formData.start_time
        : null;
    const endM =
      formData.end_time && moment.isMoment(formData.end_time) && formData.end_time.isValid()
        ? formData.end_time
        : null;
    if (!startM) {
      newErrors.start_time = 'Start time is required';
    }
    if (!endM) {
      newErrors.end_time = 'End time is required';
    }
    if (startM && endM && !endM.isAfter(startM)) {
      newErrors.end_time = 'End time must be after start time';
    }
    if (
      formData.status === 'COMPLETED'
      && checklistEnabled
      && !areRequiredChecklistItemsChecked(checklistItems, checkedIds)
    ) {
      newErrors.checklist = 'Check every required item before marking the room clean.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatTimeForApi = (val) => {
    if (!val) return null;
    if (moment.isMoment(val) && val.isValid()) return val.format('HH:mm:ss');
    if (typeof val === 'string') return val;
    return null;
  };

  const handleSave = async () => {
    if (!validateForm() || saving) return;

    setSaving(true);
    const dataToSend = {
      room: formData.room,
      staff: formData.staff,
      cleaning_date: formData.cleaning_date.format('YYYY-MM-DD'),
      start_time: formatTimeForApi(formData.start_time),
      end_time: formatTimeForApi(formData.end_time),
      cleaning_type: formData.cleaning_type,
      status: formData.status,
      remarks: formData.remarks,
      checklist_item_ids: Array.from(checkedIds),
    };
    const isEdit = !!editingCleaning;

    // Close drawer immediately so it feels instant; run save in background
    handleCloseDrawer();

    try {
      if (isEdit) {
        await updateRoomCleaning(editingCleaning.id, dataToSend);
      } else {
        await createRoomCleaning(dataToSend);
      }
      setSnackbar({
        open: true,
        message: isEdit ? 'Room cleaning updated successfully' : 'Room cleaning created successfully',
        severity: 'success',
      });
      // Refresh list and room status in background so create feels fast
      Promise.all([
        fetchCleanings(),
        onRefreshRoomStatus ? onRefreshRoomStatus() : Promise.resolve(),
      ]).catch(() => {});
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error?.checklist?.[0]
          || error?.staff?.[0]
          || error?.error
          || error.userMessage
          || 'Failed to save room cleaning',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (cleaning) => {
    setCleaningToDelete(cleaning);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteRoomCleaning(cleaningToDelete.id);
      setSnackbar({
        open: true,
        message: 'Room cleaning deleted successfully',
        severity: 'success',
      });
      setDeleteDialogOpen(false);
      setCleaningToDelete(null);
      fetchCleanings();
      onRefreshRoomStatus?.();
    } catch (error) {setSnackbar({
        open: true,
        message: error.userMessage || 'Failed to delete room cleaning',
        severity: 'error',
      });
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box>
        <Paper
          elevation={0}
          sx={{
            ...getHousekeepingListPaperSx(isDark),
            p: { xs: 1.5, sm: 2, md: 2.5 },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 600 }}>
              Room Cleanings
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDrawer()}
              sx={{
                backgroundColor: THEME_COLOR,
                color: '#000',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: THEME_COLOR,
                  opacity: 0.9,
                },
              }}
            >
              Add Cleaning
            </Button>
          </Box>

          <TextField
            fullWidth
            placeholder="Search by room number or staff name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: <CleaningServicesIcon sx={{ mr: 1, color: isDark ? '#999' : '#666' }} />,
            }}
          />

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Room</TableCell>
                      <TableCell>Staff</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cleaningList.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" sx={{ color: isDark ? '#999' : '#666', py: 2 }}>
                            No room cleanings found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      cleaningList.map((cleaning) => {
                        const roomId = typeof cleaning.room === 'object' && cleaning.room != null
                          ? cleaning.room.id
                          : (cleaning.room_id ?? cleaning.room);
                        const roomMeta = rooms.find(
                          (r) => r.id === roomId || String(r.id) === String(roomId)
                            || (cleaning.room_number && r.room_number === cleaning.room_number)
                        );
                        const roomLabel = cleaning.room_number || cleaning.room?.room_number || 'N/A';
                        const roomTypeLabel = cleaning.room_type_name || roomMeta?.room_type_name || '';
                        return (
                        <TableRow key={cleaning.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                              <Avatar
                                src={roomMeta?.image_url || undefined}
                                variant="rounded"
                                sx={{
                                  width: 40,
                                  height: 40,
                                  bgcolor: isDark ? '#1f2a28' : '#e6f9f2',
                                  color: THEME_COLOR,
                                  flexShrink: 0,
                                }}
                              >
                                <MeetingRoomRoundedIcon fontSize="small" />
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                                  {roomLabel}
                                </Typography>
                                {roomTypeLabel ? (
                                  <Typography
                                    variant="caption"
                                    sx={{ color: isDark ? '#8a939e' : '#78828c', display: 'block', lineHeight: 1.2 }}
                                  >
                                    {roomTypeLabel}
                                  </Typography>
                                ) : null}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>{cleaning.staff_name || cleaning.staff?.full_name || 'Unassigned'}</TableCell>
                          <TableCell>{cleaning.cleaning_date ? moment(cleaning.cleaning_date).format('DD/MM/YYYY') : 'N/A'}</TableCell>
                          <TableCell>
                            <CleaningTypeChip type={cleaning.cleaning_type} isDark={isDark} />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={STATUS_CHOICES.find((s) => s.value === cleaning.status)?.label || cleaning.status}
                              size="small"
                              sx={{
                                backgroundColor: cleaning.status === 'COMPLETED' ? '#4caf50' : cleaning.status === 'IN_PROGRESS' ? '#ff9800' : '#999',
                                color: '#fff',
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {cleaning.start_time && cleaning.end_time
                              ? `${cleaning.start_time} - ${cleaning.end_time}`
                              : 'N/A'}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="View">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDrawer(cleaning, 'view')}
                                sx={{
                                  color: isDark ? '#4fc3f7' : '#0288d1',
                                  '&:hover': {
                                    backgroundColor: isDark ? 'rgba(79, 195, 247, 0.12)' : 'rgba(2, 136, 209, 0.08)',
                                  },
                                }}
                              >
                                <VisibilityOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDrawer(cleaning, 'edit')}
                                sx={{ color: THEME_COLOR }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(cleaning)}
                                sx={{ color: '#f44336' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage="Rows:"
              />
            </>
          )}
        </Paper>

        {/* Add/Edit Drawer — aligned with Raise Maintenance drawer design */}
        <Drawer
          anchor="right"
          open={openDrawer}
          onClose={handleCloseDrawer}
          sx={getDrawerRootSx()}
          PaperProps={{
            sx: {
              width: { xs: '100%', sm: 600 },
              ...getHousekeepingDrawerPaperSx(isDark),
              backgroundColor: isDark ? themeColors.surface : '#ffffff',
              backgroundImage: 'none',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              ...getHousekeepingDrawerHeaderSx(isDark),
              backgroundColor: isDark ? themeColors.headerBg : '#ffffff',
              backgroundImage: 'none',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isDark ? '#1f2a28' : '#e6f9f2',
                }}
              >
                <CleaningServicesIcon sx={{ color: THEME_COLOR, fontSize: 22 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ color: isDark ? '#eee' : '#222', fontWeight: 700, lineHeight: 1.2 }}>
                  {drawerMode === 'view'
                    ? 'View Room Cleaning'
                    : drawerMode === 'edit'
                      ? 'Edit Room Cleaning'
                      : 'Add Room Cleaning'}
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? '#8a939e' : '#78828c' }}>
                  {drawerMode === 'view'
                    ? 'Read-only cleaning details'
                    : drawerMode === 'edit'
                      ? 'Update the schedule below'
                      : 'Assign a room and cleaning window'}
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={handleCloseDrawer}
              disabled={saving}
              sx={{
                color: isDark ? '#999' : '#666',
                backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <FormControl fullWidth required={!isViewMode} error={!!errors.room} sx={getSoftFieldSx(isDark)} disabled={isViewMode}>
              <InputLabel>Room</InputLabel>
              <Select
                value={formData.room}
                onChange={(e) => handleInputChange('room', e.target.value)}
                label="Room"
                disabled={isViewMode}
                renderValue={(selected) => {
                  const room = rooms.find((r) => r.id === selected || String(r.id) === String(selected));
                  if (!room) return '';
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Avatar
                        src={room.image_url || undefined}
                        variant="rounded"
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: isDark ? '#1f2a28' : '#e6f9f2',
                          color: THEME_COLOR,
                        }}
                      >
                        <MeetingRoomRoundedIcon fontSize="small" />
                      </Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {room.name}
                      </Typography>
                    </Box>
                  );
                }}
                MenuProps={{
                  ...drawerOverlays.selectMenuProps,
                  PaperProps: {
                    ...drawerOverlays.selectMenuProps.PaperProps,
                    sx: {
                      ...drawerOverlays.selectMenuProps.PaperProps?.sx,
                      maxHeight: 360,
                    },
                  },
                }}
              >
                {rooms.map((room) => (
                  <MenuItem
                    key={room.id}
                    value={room.id}
                    sx={{
                      py: 1,
                      '&.Mui-selected': {
                        backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.1)',
                      },
                    }}
                  >
                    <ListItemAvatar sx={{ minWidth: 48 }}>
                      <Avatar
                        src={room.image_url || undefined}
                        variant="rounded"
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: isDark ? '#1f2a28' : '#e6f9f2',
                          color: THEME_COLOR,
                        }}
                      >
                        <MeetingRoomRoundedIcon fontSize="small" />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={room.room_number || room.name}
                      secondary={room.room_type_name}
                      primaryTypographyProps={{ fontWeight: 700, fontSize: '0.95rem' }}
                      secondaryTypographyProps={{
                        fontSize: '0.75rem',
                        color: isDark ? '#8a939e' : '#78828c',
                      }}
                    />
                  </MenuItem>
                ))}
              </Select>
              {errors.room && <FormHelperText>{errors.room}</FormHelperText>}
            </FormControl>

            <FormControl fullWidth required={!isViewMode} error={!!errors.staff} sx={getSoftFieldSx(isDark)} disabled={isViewMode}>
              <InputLabel>Staff</InputLabel>
              <Select
                value={formData.staff === '' || formData.staff == null ? '' : formData.staff}
                onChange={(e) => handleInputChange('staff', e.target.value)}
                label="Staff"
                disabled={isViewMode}
                MenuProps={drawerOverlays.selectMenuProps}
              >
                <MenuItem value="" sx={{ display: 'none' }} aria-hidden />
                {staffList.map((staff) => (
                  <MenuItem key={staff.id} value={staff.id}>
                    {staff.full_name}
                  </MenuItem>
                ))}
              </Select>
              {errors.staff && <FormHelperText>{errors.staff}</FormHelperText>}
            </FormControl>

            <DatePicker
              label="Cleaning Date"
              format="DD MMM YYYY"
              value={formData.cleaning_date}
              onChange={(date) => handleInputChange('cleaning_date', date)}
              disabled={isViewMode}
              readOnly={isViewMode}
              slotProps={{
                ...drawerOverlays.datePickerSlotProps,
                textField: {
                  fullWidth: true,
                  required: !isViewMode,
                  error: !!errors.cleaning_date,
                  helperText: errors.cleaning_date,
                  sx: getSoftFieldSx(isDark),
                },
              }}
            />

            <Box>
              <SectionLabel required isDark={isDark}>Schedule</SectionLabel>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: '16px',
                  background: isDark
                    ? 'linear-gradient(145deg, rgba(76,225,182,0.08) 0%, rgba(255,255,255,0.03) 100%)'
                    : 'linear-gradient(145deg, rgba(76,225,182,0.1) 0%, rgba(0,0,0,0.02) 100%)',
                  border: `1px solid ${isDark ? 'rgba(76,225,182,0.18)' : 'rgba(76,225,182,0.22)'}`,
                }}
              >
                <TimePicker
                  label="Start Time"
                  value={formData.start_time}
                  onChange={(newVal) => handleInputChange('start_time', newVal)}
                  disabled={isViewMode}
                  readOnly={isViewMode}
                  ampm
                  minutesStep={5}
                  views={['hours', 'minutes']}
                  viewRenderers={{
                    hours: renderTimeViewClock,
                    minutes: renderTimeViewClock,
                  }}
                  slots={{
                    openPickerIcon: AccessTimeRoundedIcon,
                  }}
                  slotProps={{
                    ...drawerOverlays.datePickerSlotProps,
                    textField: {
                      fullWidth: true,
                      required: !isViewMode,
                      error: !!errors.start_time,
                      helperText: errors.start_time,
                      sx: getSoftFieldSx(isDark),
                    },
                    openPickerButton: {
                      disabled: isViewMode,
                      sx: {
                        color: THEME_COLOR,
                        backgroundColor: isDark ? 'rgba(76,225,182,0.12)' : 'rgba(76,225,182,0.14)',
                        borderRadius: '10px',
                        mr: 0.5,
                        '&:hover': {
                          backgroundColor: isDark ? 'rgba(76,225,182,0.22)' : 'rgba(76,225,182,0.22)',
                        },
                      },
                    },
                    desktopPaper: {
                      sx: {
                        borderRadius: '18px',
                        overflow: 'hidden',
                        border: `1px solid ${isDark ? 'rgba(76,225,182,0.25)' : 'rgba(76,225,182,0.3)'}`,
                        boxShadow: isDark
                          ? '0 18px 40px rgba(0,0,0,0.45)'
                          : '0 18px 40px rgba(15,23,42,0.14)',
                        backgroundImage: isDark
                          ? 'linear-gradient(180deg, rgba(31,42,40,1) 0%, rgba(20,28,26,1) 100%)'
                          : 'linear-gradient(180deg, #ffffff 0%, #f3fbf8 100%)',
                        '& .MuiClock-pin, & .MuiClockPointer-root, & .MuiClockPointer-thumb': {
                          backgroundColor: THEME_COLOR,
                        },
                        '& .MuiClockPointer-thumb': {
                          borderColor: THEME_COLOR,
                        },
                        '& .MuiClockNumber-root.Mui-selected': {
                          backgroundColor: THEME_COLOR,
                          color: '#04211a',
                          fontWeight: 800,
                        },
                      },
                    },
                  }}
                />
                <TimePicker
                  label="End Time"
                  value={formData.end_time}
                  onChange={(newVal) => handleInputChange('end_time', newVal)}
                  disabled={isViewMode}
                  readOnly={isViewMode}
                  minTime={
                    formData.start_time && moment.isMoment(formData.start_time)
                      ? formData.start_time.clone().add(1, 'minute')
                      : undefined
                  }
                  ampm
                  minutesStep={5}
                  views={['hours', 'minutes']}
                  viewRenderers={{
                    hours: renderTimeViewClock,
                    minutes: renderTimeViewClock,
                  }}
                  slots={{
                    openPickerIcon: AccessTimeRoundedIcon,
                  }}
                  slotProps={{
                    ...drawerOverlays.datePickerSlotProps,
                    textField: {
                      fullWidth: true,
                      required: !isViewMode,
                      error: !!errors.end_time,
                      helperText: errors.end_time,
                      sx: getSoftFieldSx(isDark),
                    },
                    openPickerButton: {
                      disabled: isViewMode,
                      sx: {
                        color: THEME_COLOR,
                        backgroundColor: isDark ? 'rgba(76,225,182,0.12)' : 'rgba(76,225,182,0.14)',
                        borderRadius: '10px',
                        mr: 0.5,
                        '&:hover': {
                          backgroundColor: isDark ? 'rgba(76,225,182,0.22)' : 'rgba(76,225,182,0.22)',
                        },
                      },
                    },
                    desktopPaper: {
                      sx: {
                        borderRadius: '18px',
                        overflow: 'hidden',
                        border: `1px solid ${isDark ? 'rgba(76,225,182,0.25)' : 'rgba(76,225,182,0.3)'}`,
                        boxShadow: isDark
                          ? '0 18px 40px rgba(0,0,0,0.45)'
                          : '0 18px 40px rgba(15,23,42,0.14)',
                        backgroundImage: isDark
                          ? 'linear-gradient(180deg, rgba(31,42,40,1) 0%, rgba(20,28,26,1) 100%)'
                          : 'linear-gradient(180deg, #ffffff 0%, #f3fbf8 100%)',
                        '& .MuiClock-pin, & .MuiClockPointer-root, & .MuiClockPointer-thumb': {
                          backgroundColor: THEME_COLOR,
                        },
                        '& .MuiClockPointer-thumb': {
                          borderColor: THEME_COLOR,
                        },
                        '& .MuiClockNumber-root.Mui-selected': {
                          backgroundColor: THEME_COLOR,
                          color: '#04211a',
                          fontWeight: 800,
                        },
                      },
                    },
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ opacity: isViewMode ? 0.95 : 1 }}>
              <SectionLabel required={!isViewMode} isDark={isDark}>Cleaning Type</SectionLabel>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pointerEvents: isViewMode ? 'none' : 'auto' }}>
                {CLEANING_TYPE_CHOICES.map((type) => (
                  <SelectableChip
                    key={type.value}
                    label={type.label}
                    icon={type.icon}
                    selected={formData.cleaning_type === type.value}
                    color={type.color}
                    isDark={isDark}
                    onClick={() => !isViewMode && handleInputChange('cleaning_type', type.value)}
                  />
                ))}
              </Box>
              {errors.cleaning_type && (
                <FormHelperText error sx={{ mx: 0, mt: 0.75 }}>{errors.cleaning_type}</FormHelperText>
              )}
            </Box>

            <Box>
              <SectionLabel isDark={isDark}>Status</SectionLabel>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pointerEvents: (isViewMode || checklistEnabled) ? 'none' : 'auto' }}>
                {STATUS_CHOICES.map((status) => (
                  <SelectableChip
                    key={status.value}
                    label={status.label}
                    icon={status.icon}
                    selected={formData.status === status.value}
                    color={status.color}
                    isDark={isDark}
                    onClick={() => !isViewMode && !checklistEnabled && handleInputChange('status', status.value)}
                  />
                ))}
              </Box>
              {checklistEnabled ? (
                <FormHelperText sx={{ mx: 0, mt: 0.75 }}>
                  Status follows the checklist: pending, in progress, then completed when every required item is checked.
                </FormHelperText>
              ) : null}
            </Box>

            {showCleaningChecklist ? (
              <HousekeepingCleaningChecklist
                items={visibleChecklistItems}
                checkedIds={checkedIds}
                isDark={isDark}
                error={errors.checklist}
                status={checklistEnabled ? formData.status : ''}
                disabled={isViewMode || (hasCapturedChecklist && !checklistEnabled)}
                onToggle={(itemId) => {
                  const next = new Set(checkedIds);
                  if (next.has(itemId)) next.delete(itemId);
                  else next.add(itemId);
                  setCheckedIds(next);
                  if (checklistEnabled) {
                    const derived = cleaningStatusFromChecklist({
                      checklistEnabled,
                      items: visibleChecklistItems,
                      checkedIds: next,
                    });
                    if (derived) handleInputChange('status', derived);
                  }
                  setErrors((prev) => ({ ...prev, checklist: null }));
                }}
              />
            ) : null}

            <TextField
              label="Remarks"
              fullWidth
              multiline
              rows={4}
              value={formData.remarks}
              onChange={(e) => handleInputChange('remarks', e.target.value)}
              InputProps={{ readOnly: isViewMode }}
              sx={getSoftFieldSx(isDark)}
            />
          </Box>

          <Box
            sx={{
              p: 1.5,
              ...getHousekeepingDrawerFooterSx(isDark),
              backgroundColor: isDark ? themeColors.headerBg : '#ffffff',
              backgroundImage: 'none',
              display: 'flex',
              gap: 1.5,
            }}
          >
            <Button
              variant="outlined"
              fullWidth
              onClick={handleCloseDrawer}
              disabled={saving}
              sx={getDrawerCancelButtonSx(isDark, themeColors.surfaceBorder)}
            >
              {isViewMode ? 'Close' : 'Cancel'}
            </Button>
            {isViewMode ? (
              <Button
                variant="contained"
                fullWidth
                onClick={() => setDrawerMode('edit')}
                startIcon={<EditIcon />}
                sx={getDrawerPrimaryButtonSx()}
              >
                Edit
              </Button>
            ) : (
              <Button
                variant="contained"
                fullWidth
                onClick={handleSave}
                disabled={
                  saving
                  || (
                    formData.status === 'COMPLETED'
                    && checklistEnabled
                    && !areRequiredChecklistItemsChecked(checklistItems, checkedIds)
                  )
                }
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{
                  ...getDrawerPrimaryButtonSx(),
                  '&.Mui-disabled': {
                    background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                    color: isDark ? '#777' : '#999',
                    boxShadow: 'none',
                  },
                }}
              >
                {saving ? 'Saving…' : editingCleaning ? 'Update' : 'Room Clean'}
              </Button>
            )}
          </Box>
        </Drawer>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Room Cleaning</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this room cleaning? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default RoomCleaningSection;

